import React, { useState, useEffect } from 'react';
import { Users, AlertCircle, CheckCircle, UserPlus, Share, Eye } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';

export default function PatientInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assigneeId, setAssigneeId] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      
      const inquiriesRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/patient-inquiries`, {
        headers: { }
      });
      if (inquiriesRes.ok) {
        const data = await inquiriesRes.json();
        setInquiries(data.inquiries || []);
      }

      const adminsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/team-members`, {
        headers: { }
      });
      if (adminsRes.ok) {
        const data = await adminsRes.json();
        setAdmins(data.admins || []);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleAssignPatient = async () => {
    if (!selectedInquiry || !assigneeId) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/patient-inquiries/${selectedInquiry._id}/assign`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ assigned_to: assigneeId })
      });

      if (response.ok) {
        toast.success('Patient inquiry assigned successfully');
        setShowAssignDialog(false);
        setSelectedInquiry(null);
        setAssigneeId('');
        fetchData();
      } else {
        toast.error('Failed to assign inquiry');
      }
    } catch (err) {
      toast.error('Error assigning inquiry');
    }
  };

  const handlePublishToClassified = async (inquiryId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/patient-inquiries/${inquiryId}/publish`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ publish_to_classified: true })
      });

      if (response.ok) {
        toast.success('Inquiry published to classified page');
        fetchData();
      } else {
        toast.error('Failed to publish inquiry');
      }
    } catch (err) {
      toast.error('Error publishing inquiry');
    }
  };

  // Removed unused functions and filters

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-6xl mb-4">⏳</div>
          <p className="text-gray-600">Loading patient inquiries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="patient-inquiries">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Patient Inquiries</h1>
          <p className="text-gray-600 mt-1">Manage incoming patient treatment requests</p>
        </div>
        <Badge className="bg-blue-100 text-blue-800 px-4 py-2 text-lg">
          {inquiries.length} Total Inquiries
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-2 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">New Inquiries</p>
                <p className="text-2xl font-bold text-gray-900">
                  {inquiries.filter(i => i.status === 'new').length}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Assigned</p>
                <p className="text-2xl font-bold text-gray-900">
                  {inquiries.filter(i => i.status === 'assigned').length}
                </p>
              </div>
              <UserPlus className="w-8 h-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Published</p>
                <p className="text-2xl font-bold text-gray-900">
                  {inquiries.filter(i => i.status === 'published').length}
                </p>
              </div>
              <Share className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Urgent</p>
                <p className="text-2xl font-bold text-gray-900">
                  {inquiries.filter(i => i.priority === 'urgent' || i.priority === 'high' || i.preferred_timeline?.includes('Urgent')).length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inquiries Table */}
      <Card>
        <CardContent className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">Patient</th>
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">Treatment</th>
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">Priority</th>
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">Status</th>
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {inquiries.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="py-8 text-center text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p>No patient inquiries found</p>
                    </td>
                  </tr>
                ) : (
                  inquiries.map((inquiry) => (
                    <tr key={inquiry._id || inquiry.inquiry_id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-4">
                        <div>
                          <p className="font-semibold text-gray-900">{inquiry.full_name || inquiry.patient_name}</p>
                          <p className="text-sm text-gray-500">{inquiry.email || inquiry.patient_email}</p>
                          <p className="text-xs text-gray-400">{inquiry.country_name || inquiry.patient_country}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div>
                          <p className="font-medium text-gray-900">{inquiry.treatment_name || inquiry.treatment_type}</p>
                          <p className="text-sm text-gray-500 truncate max-w-xs">{inquiry.medical_condition || inquiry.condition_description}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <Badge className={
                          inquiry.priority === 'urgent' || inquiry.preferred_timeline === 'Urgent (Within 2 weeks)' ? 'bg-red-100 text-red-800' :
                          inquiry.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                          inquiry.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }>
                          {inquiry.priority || (inquiry.preferred_timeline?.includes('Urgent') ? 'urgent' : 'normal')}
                        </Badge>
                      </td>
                      <td className="py-4 px-4">
                        <Badge className={
                          inquiry.status === 'new' ? 'bg-blue-100 text-blue-800' :
                          inquiry.status === 'assigned' ? 'bg-yellow-100 text-yellow-800' :
                          inquiry.status === 'published' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }>
                          {inquiry.status || 'new'}
                        </Badge>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600 hover:bg-blue-50"
                            data-testid="view-inquiry"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          
                          {(inquiry.status === 'new' || !inquiry.status) && (
                            <>
                              <Button
                                size="sm"
                                className="bg-yellow-600 hover:bg-yellow-700 text-white"
                                onClick={() => {
                                  setSelectedInquiry(inquiry);
                                  setShowAssignDialog(true);
                                }}
                                data-testid="assign-inquiry"
                              >
                                <UserPlus className="w-4 h-4" />
                              </Button>
                              
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => handlePublishToClassified(inquiry._id || inquiry.inquiry_id)}
                                data-testid="publish-inquiry"
                              >
                                <Share className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Assignment Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Patient Inquiry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedInquiry && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-semibold text-gray-900">{selectedInquiry.full_name || selectedInquiry.patient_name}</p>
                <p className="text-sm text-gray-600">{selectedInquiry.treatment_name || selectedInquiry.treatment_type}</p>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Assign to Admin</label>
              <select
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Select Admin</option>
                {admins.map((admin) => (
                  <option key={admin._id} value={admin._id}>
                    {admin.name} ({admin.role})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAssignDialog(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAssignPatient}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={!assigneeId}
              >
                Assign Inquiry
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}